import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { categoriesService, Category } from '../../services/categories.service';

/**
 * Query keys para cache do React Query
 */
export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (filters?: Record<string, any>) => [...categoryKeys.lists(), { filters }] as const,
  details: () => [...categoryKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...categoryKeys.details(), id] as const,
};

/**
 * Hook para buscar todas as categorias com cache
 */
export function useCategories(options?: Omit<UseQueryOptions<Category[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery<Category[], Error>({
    queryKey: categoryKeys.list(),
    queryFn: () => categoriesService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutos - dados considerados frescos
    gcTime: 10 * 60 * 1000, // 10 minutos - tempo de garbage collection (anteriormente cacheTime)
    ...options,
  });
}

/**
 * Hook para buscar uma categoria por ID com cache
 */
export function useCategory(
  id: string | number | null | undefined,
  options?: Omit<UseQueryOptions<Category, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Category, Error>({
    queryKey: categoryKeys.detail(id!),
    queryFn: () => categoriesService.getById(id!),
    enabled: !!id, // Só executa se id existir
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook para criar uma nova categoria (mutation com invalidação de cache)
 */
export function useCreateCategory(
  options?: UseMutationOptions<Category, Error, Omit<Category, 'id' | 'created_at' | 'updated_at'>>
) {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, Omit<Category, 'id' | 'created_at' | 'updated_at'>>({
    mutationFn: (data) => categoriesService.create(data),
    onSuccess: () => {
      // Invalida todas as queries de categorias para forçar refetch
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
    ...options,
  });
}

/**
 * Hook para atualizar uma categoria (mutation com invalidação de cache)
 */
export function useUpdateCategory(
  options?: UseMutationOptions<Category, Error, { id: string | number; data: Partial<Category> }>
) {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { id: string | number; data: Partial<Category> }>({
    mutationFn: ({ id, data }) => categoriesService.update(id, data),
    onSuccess: (data, variables) => {
      // Invalida todas as queries de categorias
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      
      // Atualiza o cache da categoria específica otimisticamente
      queryClient.setQueryData(categoryKeys.detail(variables.id), data);
    },
    ...options,
  });
}

/**
 * Hook para deletar uma categoria (mutation com invalidação de cache)
 */
export function useDeleteCategory(options?: UseMutationOptions<void, Error, string | number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: (id) => categoriesService.delete(id),
    onSuccess: (_, id) => {
      // Remove a categoria específica do cache
      queryClient.removeQueries({ queryKey: categoryKeys.detail(id) });
      
      // Invalida a lista de categorias para refetch
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
    },
    ...options,
  });
}

